/**
 * The error envelope every failing route returns (§5, "return typed errors").
 *
 * One shape for every failure means the client has exactly one thing to parse,
 * and `code` — not the human message — is what it branches on.
 */

import { z } from 'zod';

export const ERROR_CODES = [
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'validation_failed',
  'rate_limited',
  'internal',
] as const;

export const errorCode = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCode>;

export const HTTP_STATUS_BY_CODE: Record<ErrorCode, number> = {
  bad_request: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  validation_failed: 422,
  rate_limited: 429,
  internal: 500,
};

export const apiError = z.object({
  error: z.object({
    code: errorCode,
    /** Safe to show a user. Never contains ids, SQL, or stack detail. */
    message: z.string(),
    /** Field-level detail, present only for `validation_failed`. */
    issues: z
      .array(
        z.object({
          path: z.string(),
          message: z.string(),
        }),
      )
      .optional(),
  }),
});
export type ApiError = z.infer<typeof apiError>;

/**
 * Why `not_found` and not `forbidden` for another tenant's row: a 403 confirms
 * the id exists. Everything outside the caller's garages is simply absent, so
 * ids can't be probed (§11).
 */
export const NOT_FOUND_MESSAGE = 'Not found';
